import type { HistoryEntry, HistoryKind, NoteAudience, NoteSharing } from "@shyne/shared/api";

export interface AppointmentLogJoin {
  id: string;
  kind: HistoryKind;
  message: string;
  created_at: string;
  user: { id: string; name: string | null } | null;
  audience?: NoteSharing;
  reasons?: string[];
  priceChange?: HistoryEntry["priceChange"];
}

export const toAppointmentLog = (entry: HistoryEntry): AppointmentLogJoin => ({
  id: entry.id,
  kind: entry.kind,
  message: entry.message,
  created_at: entry.at,
  user: entry.author === null ? null : { id: entry.author.id, name: entry.author.name },
  ...(entry.audience === undefined ? {} : { audience: entry.audience }),
  ...(entry.reasons === undefined ? {} : { reasons: entry.reasons }),
  ...(entry.priceChange === undefined ? {} : { priceChange: entry.priceChange }),
});

const endsWithReason = (message: string, reason: string): boolean => message.trimEnd().endsWith(`: ${reason.trim()}`);

export const reasonsNotInTheMessage = ({ message, reasons = [] }: Pick<AppointmentLogJoin, "message" | "reasons">): string[] => reasons.filter((reason) => !endsWithReason(message, reason));

const dollars = (amount: number | null) => (amount === null ? "no price" : `$${amount.toFixed(2)}`);

export const priceChangeText = ({ from, to }: NonNullable<AppointmentLogJoin["priceChange"]>): string => `Price: ${dollars(from)} → ${dollars(to)}`;

export const isNote = (log: Pick<AppointmentLogJoin, "kind">): boolean => log.kind === "note";

export const NOTE_AUDIENCE_OPTIONS: readonly { id: NoteAudience; label: string }[] = [
  { id: "customer", label: "PMC" },
  { id: "subcontractor", label: "Subs" },
  { id: "worker", label: "Individuals" },
];

export const INTERNAL_NOTE_HINT = "Shared with (check none to keep it internal, for your office only):";

export function audienceLabel(audience: NoteSharing): string {
  if (audience === "internal") return "Internal";
  if (audience === "everyone") return "Everyone";
  return NOTE_AUDIENCE_OPTIONS.filter((option) => audience.includes(option.id))
    .map((option) => option.label)
    .join(", ");
}
